import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { colors } from '../../../core';
import Heading from '../Heading';
import Icon from '../Icon';
import Text from '../Text';

export interface DropdownProps {
  options: Option[];
  heading?: string;
  width?: string;
  onChange: (option: Option) => void;
  selected: Option;
}

export interface Option {
  value: string;
  label: string;
}

const DropDownContainer = styled.div<{ width?: string }>`
  padding-right: 10px;
  position: relative;
  width: ${({ width }) => (width ? width : '100%')};
`;

const DropDownHeader = styled.div`
  display: flex;
  align-items: center;
  &:hover {
    p {
      text-decoration: underline;
    }
  }
`;

const DropDownLabel = styled(Text)`
  font-weight: bold;
  margin-bottom: 0;
  padding-left: 0;
  padding: 0 6px;
  cursor: pointer;
`;

const DropDownListContainer = styled.div<{ showHeading?: boolean }>`
  position: absolute;
  z-index: 1;
  min-width: 216px;
  top: 30px;
  padding: ${({ showHeading }) => (!showHeading ? '8px 0' : '20px 0 8px')};
  left: 0;
  background-color: ${colors.white};
  border: 1px solid rgba(221, 221, 221, 0.4);
  border-radius: 4px;
  box-shadow: 0 2px 6px 0 rgba(233, 233, 233, 0.5);
`;

const DropDownList = styled.ul`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  text-align: left;
`;

const ListItem = styled.li`
  list-style: none;
  padding: 8px 20px;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: ${colors.pearl};
  }
`;

const ListItemText = styled(Text)`
  margin-bottom: 0;
`;

const ListHeading = styled(Heading)`
  padding: 0 20px;
`;

const StyledIcon = styled(Icon).attrs(({ theme }) => ({
  color: theme.dropdownIconColor || colors.primary,
}))`
  cursor: pointer;
`;

const Dropdown = ({ options, heading, width, onChange, selected }: DropdownProps) => {
  const ref = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);

  const toggling = (e: any) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleListClick = (e: any) => {
    e.stopPropagation();
  };

  const onOptionClicked = (option: Option) => () => {
    setIsOpen(false);
    onChange(option);
  };

  const handleEscape = useCallback((keyboardEvent) => {
    if (keyboardEvent.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  const handleClickOutside = useCallback((clickEvent) => {
    if (ref && ref.current && !ref.current.contains(clickEvent.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keyup', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keyup', handleEscape);
    };
  }, [handleClickOutside, handleEscape]);

  return (
    <DropDownContainer ref={ref} width={width}>
      <DropDownHeader onClick={(e: any) => toggling(e)}>
        <DropDownLabel size="small">{selected.label}</DropDownLabel>
        <StyledIcon size={12} type={isOpen ? 'caret-up' : 'caret-down'} />
      </DropDownHeader>
      {isOpen && (
        <DropDownListContainer showHeading={!!heading}>
          {heading ? <ListHeading smallCaps>{heading}</ListHeading> : null}

          <DropDownList onClick={(e: any) => handleListClick(e)}>
            {options.map((option) => (
              <ListItem onClick={onOptionClicked(option)} key={option.value}>
                {option.value === selected.value ? (
                  <ListItemText size="small">
                    <b>{option.label}</b>{' '}
                  </ListItemText>
                ) : (
                  <ListItemText size="small">{option.label}</ListItemText>
                )}
              </ListItem>
            ))}
          </DropDownList>
        </DropDownListContainer>
      )}
    </DropDownContainer>
  );
};
export default Dropdown;
